import { useState } from "react";
import type { CaseRecord, RecordFact } from "../../types";
import type { PartyUi } from "../../lib/parties";
import FactCard from "./FactCard";
import { formatFactDate } from "../../lib/factMeta";

// A collapsible group of facts that all came from ONE record, shown under
// the record's subject. Reduces clutter vs. a flat fact list (Part D #4).
// Expanded by default for key (high-significance) records.
export default function RecordFactGroup({
  record,
  facts,
  onNavigateToRecord,
  onViewQuote,
  alsoInByFactId,
  recordById,
  groupDate,
  partyUi,
}: {
  record?: CaseRecord;
  facts: RecordFact[];
  onNavigateToRecord?: (recordId: string) => void;
  onViewQuote?: (fact: RecordFact) => void;
  // Per-fact "also in these other records" map + the lookup to label them,
  // for deduplicated facts. Omitted when dedup isn't in play.
  alsoInByFactId?: Map<string, string[]>;
  recordById?: Map<string, CaseRecord>;
  // The chronological date this group renders under (the FACT date). When
  // it differs from the record's own date, the header says so — a record
  // title under a date a month before the record exists is the confusing
  // case ("mentioned in" context, group-header form).
  groupDate?: string | null;
  partyUi?: PartyUi;
}) {
  const high = record?.significance === "high";
  const [open, setOpen] = useState(high);
  const title =
    record?.subject?.trim() ||
    (record ? `${record.source_type} — ${record.from_addr ?? "?"}` : "Record");
  const recDate = record?.record_date?.slice(0, 10);
  const dateContext =
    !!groupDate && !!recDate && groupDate.slice(0, 10) !== recDate;

  return (
    <div className="fact-rec-group">
      <button
        type="button"
        className="fact-rec-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="fact-rec-caret">{open ? "▾" : "▸"}</span>
        <span className="fact-rec-title">
          {dateContext ? "mentioned in " : ""}
          {title}
          {dateContext && (
            <span className="overview-muted"> ({formatFactDate(recDate!)})</span>
          )}
        </span>
        <span className="fact-rec-count">
          {facts.length} fact{facts.length === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <div className="fact-rec-body">
          {facts.map((f) => (
            <FactCard
              key={f.id}
              fact={f}
              record={record}
              onNavigateToRecord={onNavigateToRecord}
              onViewQuote={onViewQuote}
              defaultExpanded={high}
              hideSource
              alsoIn={alsoInByFactId?.get(f.id) ?? []}
              recordById={recordById}
              partyUi={partyUi}
            />
          ))}
        </div>
      )}
      {record && (
        <button
          type="button"
          className="fact-source-link fact-rec-source"
          onClick={() => onNavigateToRecord?.(record.id)}
        >
          View source record →
        </button>
      )}
    </div>
  );
}
